import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { bootAnalytics, grantConsent } from "@/lib/analytics";
import { bootMetaPixel } from "@/lib/metaPixel";
import { initClickTracking } from "@/lib/clickTracking";

const KEY = "dh-consent";

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    // gtag lädt immer (Consent Mode v2, Standard: denied → anonyme Pings,
    // Google modelliert Conversions). "accepted" hebt auf granted an.
    bootAnalytics();
    bootMetaPixel();
    initClickTracking();
    try {
      const stored = localStorage.getItem(KEY);
      if (!stored) {
        const t = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  const accept = () => {
    try { localStorage.setItem(KEY, "accepted"); } catch {}
    grantConsent();
    bootMetaPixel();
    setOpen(false);
  };
  const decline = () => {
    try { localStorage.setItem(KEY, "declined"); } catch {}
    setOpen(false);
  };

  if (!open) return null;
  // Don't overlay auth/protected pages – it covers form/CTAs.
  const isAuthRoute =
    pathname.startsWith("/campus/app") ||
    pathname.startsWith("/campus/login") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/verify-email");
  if (isAuthRoute) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      className="fixed inset-x-3 bottom-3 md:bottom-5 md:left-5 md:right-auto md:max-w-md z-[60] animate-fade-up"
    >
      <div className="relative rounded-3xl bg-cream-50 border border-cream-300 shadow-hover p-5 md:p-6">
        <button
          onClick={decline}
          aria-label="Banner schließen"
          className="absolute top-3 right-3 inline-flex h-7 w-7 items-center justify-center rounded-full text-sand-500 hover:bg-cream-200"
        >
          <X className="h-4 w-4" strokeWidth={1.6} />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-rose text-white shrink-0">
            <Cookie className="h-5 w-5" strokeWidth={1.6} />
          </span>
          <div>
            <h3 className="font-heading text-base md:text-lg font-semibold text-sand-800">
              Cookies & Datenschutz
            </h3>
            <p className="mt-1.5 text-xs md:text-[13px] text-sand-600 leading-relaxed">
              Wir nutzen technisch notwendige Cookies sowie – mit deiner Einwilligung – Marketing- und Analyse-Cookies (Google Ads, GA4), um unsere Seite zu verbessern. Du kannst die Einwilligung jederzeit in der{" "}
              <a href="/datenschutz" className="underline hover:text-sand-800">Datenschutzerklärung</a> widerrufen.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Button
                data-testid="cookie-accept"
                onClick={accept}
                className="rounded-full bg-terra text-white hover:bg-terra-dark h-10 px-5 text-sm font-medium"
              >
                <ShieldCheck className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
                Alle akzeptieren
              </Button>
              <Button
                data-testid="cookie-decline"
                onClick={decline}
                variant="outline"
                className="rounded-full border-sand-300 text-sand-800 hover:bg-cream-200 h-10 px-5 text-sm font-medium bg-transparent"
              >
                Nur notwendige
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
